/**
 * Tiny SVG chart helpers. No external library — keeps the bundle small.
 */
import type { CSSProperties } from "react";

export interface BarDatum {
  label: string;
  value: number;
  /** Optional secondary value shown as a smaller stacked bar (e.g. tips). */
  value2?: number;
  /** Override the formatted display, otherwise we use locale-string. */
  display?: string;
}

interface ChartProps {
  data: BarDatum[];
  height?: number;
  /** Format the axis values; defaults to integer with locale separators. */
  fmt?: (n: number) => string;
  color?: string;
  color2?: string;
  /** Show value labels above each bar. */
  showValues?: boolean;
}

// Точна сума до 1 грн з розділювачем тисяч ("16 057", не "16k") — щоб
// підписи над стовпчиками збігались із сумами у плитках/звітах.
const defaultFmt = (n: number) =>
  String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, " ");

export function VerticalBars({
  data,
  height = 200,
  fmt = defaultFmt,
  color = "#7c3aed",
  color2,
  showValues = true,
}: ChartProps) {
  if (data.length === 0) return <Empty />;
  const max = Math.max(1, ...data.map((d) => d.value + (d.value2 ?? 0)));
  // Reserve room: top for the value labels, bottom for the month labels.
  const VALUE_LABEL_PX = 18;
  const X_LABEL_PX = 18;
  const barAreaPx = height - VALUE_LABEL_PX - X_LABEL_PX;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "stretch",
        gap: 4,
        height,
        width: "100%",
      }}
    >
      {data.map((d) => {
        const total = d.value + (d.value2 ?? 0);
        const totalPx = (total / max) * barAreaPx;
        const tipPx = ((d.value2 ?? 0) / max) * barAreaPx;
        return (
          <div
            key={d.label}
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-end",
              alignItems: "center",
              minWidth: 0,
              gap: 2,
            }}
          >
            {showValues && (
              <div
                style={{
                  fontSize: 11,
                  opacity: 0.7,
                  whiteSpace: "nowrap",
                }}
              >
                {d.display ?? fmt(total)}
              </div>
            )}
            <div
              style={{
                width: "75%",
                height: totalPx,
                display: "flex",
                flexDirection: "column",
                borderRadius: 4,
                overflow: "hidden",
              }}
            >
              {tipPx > 0 && color2 && (
                <div data-nz-bar="muted" style={{ height: tipPx, background: color2 }} />
              )}
              <div data-nz-bar style={{ flex: 1, background: color }} />
            </div>
            <div
              style={{
                fontSize: 11,
                opacity: 0.6,
                height: X_LABEL_PX,
                display: "flex",
                alignItems: "center",
              }}
            >
              {d.label}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function HorizontalBars({
  data,
  fmt = (n) => n.toLocaleString("uk-UA"),
}: {
  data: BarDatum[];
  fmt?: (n: number) => string;
}) {
  if (data.length === 0) return <Empty />;
  const max = Math.max(1, ...data.map((d) => d.value));
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      {data.map((d) => {
        const pct = (d.value / max) * 100;
        return (
          <div key={d.label} style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span
              style={{
                width: 116,
                flexShrink: 0,
                fontSize: 13,
                lineHeight: 1.15,
                overflowWrap: "anywhere",
              }}
            >
              {d.label}
            </span>
            <div style={{ flex: 1, position: "relative", height: 20 }}>
              <div
                data-nz-bar-track
                style={{
                  position: "absolute",
                  inset: 0,
                  background: "rgba(127,127,127,0.15)",
                  borderRadius: 4,
                }}
              />
              <div
                data-nz-bar
                style={{
                  position: "absolute",
                  top: 0,
                  bottom: 0,
                  left: 0,
                  width: `${pct}%`,
                  background: "linear-gradient(90deg, #a78bfa, #7c3aed)",
                  borderRadius: 4,
                }}
              />
              <span style={valueLabel}>{d.display ?? fmt(d.value)}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function StatCard({
  label,
  value,
  hint,
  color = "var(--button)",
}: {
  label: string;
  value: string | number;
  hint?: string;
  color?: string;
}) {
  return (
    <div style={statCard}>
      <div className="hint" style={{ fontSize: 12 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color, marginTop: 2 }}>
        {typeof value === "number" ? value.toLocaleString("uk-UA") : value}
      </div>
      {hint && (
        <div className="hint" style={{ fontSize: 11, marginTop: 2 }}>{hint}</div>
      )}
    </div>
  );
}

function Empty() {
  return <div className="hint" style={{ padding: 16, textAlign: "center" }}>Нема даних</div>;
}

const statCard: CSSProperties = {
  background: "var(--secondary-bg)",
  borderRadius: 10,
  padding: 10,
  minWidth: 0,
};

const valueLabel: CSSProperties = {
  position: "absolute",
  right: 6,
  top: 0,
  bottom: 0,
  display: "flex",
  alignItems: "center",
  fontSize: 12,
  fontWeight: 600,
  color: "white",
  mixBlendMode: "difference",
};
